import React, { useState, useEffect } from 'react';
import {
  X,
  TrendingUp,
  MousePointer,
  Users,
  Globe,
  Calendar,
  Download,
} from 'lucide-react';
import { AdsService, Ad, AdAnalytics } from '../services/ads';

interface AdAnalyticsModalProps {
  ad: Ad | null;
  isOpen: boolean;
  onClose: () => void;
}

export const AdAnalyticsModal: React.FC<AdAnalyticsModalProps> = ({
  ad,
  isOpen,
  onClose,
}) => {
  const [analytics, setAnalytics] = useState<AdAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    if (isOpen && ad) {
      loadAnalytics();
    }
  }, [isOpen, ad, timeRange]);

  const loadAnalytics = async () => {
    if (!ad) return;

    setLoading(true);
    try {
      // Convert timeRange to date range
      const endDate = new Date();
      const startDate = new Date();
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      startDate.setDate(endDate.getDate() - days);

      const query = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      };

      const data = await AdsService.getAnalytics(ad.id, query);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  if (!isOpen || !ad) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b'>
          <div>
            <h2 className='text-xl font-semibold text-gray-900'>
              Analytics - {ad.name}
            </h2>
            <p className='text-sm text-gray-600'>
              Detailed performance metrics and insights
            </p>
          </div>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700 transition-colors'
          >
            <X className='h-6 w-6' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* Time Range Selector */}
          <div className='mb-6'>
            <div className='flex space-x-2'>
              {(['7d', '30d', '90d'] as const).map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    timeRange === range
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range === '7d'
                    ? 'Last 7 days'
                    : range === '30d'
                      ? 'Last 30 days'
                      : 'Last 90 days'}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            </div>
          ) : analytics ? (
            <div className='space-y-6'>
              {/* Key Metrics */}
              <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                <div className='card-enhanced p-4'>
                  <div className='flex items-center'>
                    <MousePointer className='h-8 w-8 text-blue-500' />
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-gray-500'>
                        Total Clicks
                      </p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {formatNumber(analytics.totalClicks)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className='card-enhanced p-4'>
                  <div className='flex items-center'>
                    <Users className='h-8 w-8 text-green-500' />
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-gray-500'>
                        Unique Visitors
                      </p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {formatNumber(analytics.uniqueUsers)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className='card-enhanced p-4'>
                  <div className='flex items-center'>
                    <TrendingUp className='h-8 w-8 text-purple-500' />
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-gray-500'>CTR</p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {analytics.totalClicks > 0
                          ? formatPercentage(
                              analytics.uniqueUsers / analytics.totalClicks
                            )
                          : '0%'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className='card-enhanced p-4'>
                  <div className='flex items-center'>
                    <Globe className='h-8 w-8 text-orange-500' />
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-gray-500'>
                        Countries
                      </p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {analytics.clicksByCountry.length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Placeholder */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Clicks Over Time */}
                <div className='card-enhanced p-4'>
                  <h3 className='text-lg font-medium text-gray-900 mb-4'>
                    <Calendar className='inline h-5 w-5 mr-2' />
                    Clicks Over Time
                  </h3>
                  <div className='h-64 bg-gray-50 rounded-lg flex items-center justify-center'>
                    <p className='text-gray-500'>
                      Chart placeholder - Daily clicks
                    </p>
                  </div>
                </div>

                {/* Device Breakdown */}
                <div className='card-enhanced p-4'>
                  <h3 className='text-lg font-medium text-gray-900 mb-4'>
                    <MousePointer className='inline h-5 w-5 mr-2' />
                    Device Types
                  </h3>
                  <div className='space-y-3'>
                    {analytics.clicksByDevice.map((device, index) => (
                      <div
                        key={device.device}
                        className='flex items-center justify-between'
                      >
                        <span className='text-sm text-gray-600 capitalize'>
                          {device.device}
                        </span>
                        <div className='flex items-center space-x-2'>
                          <div className='w-24 bg-gray-200 rounded-full h-2'>
                            <div
                              className={`h-2 rounded-full ${
                                index === 0
                                  ? 'bg-blue-500'
                                  : index === 1
                                    ? 'bg-green-500'
                                    : 'bg-purple-500'
                              }`}
                              style={{
                                width: `${(device.clicks / analytics.totalClicks) * 100}%`,
                              }}
                            ></div>
                          </div>
                          <span className='text-sm font-medium text-gray-900'>
                            {device.clicks}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Countries and Referrers */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                <div className='card-enhanced p-4'>
                  <h3 className='text-lg font-medium text-gray-900 mb-4'>
                    <Globe className='inline h-5 w-5 mr-2' />
                    Top Countries
                  </h3>
                  <div className='space-y-2'>
                    {analytics.clicksByCountry
                      .slice(0, 5)
                      .map((country, index) => (
                        <div
                          key={country.country}
                          className='flex items-center justify-between py-2'
                        >
                          <span className='text-sm text-gray-600'>
                            {country.country || 'Unknown'}
                          </span>
                          <span className='text-sm font-medium text-gray-900'>
                            {country.clicks} clicks
                          </span>
                        </div>
                      ))}
                  </div>
                </div>

                <div className='card-enhanced p-4'>
                  <h3 className='text-lg font-medium text-gray-900 mb-4'>
                    <TrendingUp className='inline h-5 w-5 mr-2' />
                    Top Referrers
                  </h3>
                  <div className='space-y-2'>
                    {analytics.topReferrers
                      .slice(0, 5)
                      .map((referrer, index) => (
                        <div
                          key={referrer.referrer || 'direct'}
                          className='flex items-center justify-between py-2'
                        >
                          <span className='text-sm text-gray-600 truncate'>
                            {referrer.referrer || 'Direct'}
                          </span>
                          <span className='text-sm font-medium text-gray-900'>
                            {referrer.clicks} clicks
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className='card-enhanced p-4'>
                <h3 className='text-lg font-medium text-gray-900 mb-4'>
                  Recent Activity
                </h3>
                <div className='space-y-3'>
                  {analytics.recentEvents.slice(0, 10).map((event, index) => (
                    <div
                      key={index}
                      className='flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0'
                    >
                      <div className='flex items-center space-x-3'>
                        <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                        <div>
                          <p className='text-sm text-gray-900'>
                            Click from {event.countryCode || 'Unknown location'}
                          </p>
                          <p className='text-xs text-gray-500'>
                            {event.deviceType} • {event.referrer || 'Direct'}
                          </p>
                        </div>
                      </div>
                      <span className='text-xs text-gray-500'>
                        {new Date(event.createdAt).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className='text-center py-12'>
              <p className='text-gray-500'>No analytics data available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='flex justify-end space-x-3 p-6 border-t bg-gray-50'>
          <button onClick={onClose} className='btn-enhanced btn-secondary'>
            Close
          </button>
          {/* {analytics && (
            <button
              onClick={() => {
                // Export functionality could be added here
                console.log('Export analytics data:', analytics);
              }}
              className='btn-enhanced btn-primary'
            >
              <Download className='h-4 w-4 mr-2' />
              Export Data
            </button>
          )} */}
        </div>
      </div>
    </div>
  );
};
